import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  startOfMonth, endOfMonth, addMonths, startOfWeek, endOfWeek, addWeeks,
  startOfDay, endOfDay, addDays, format, isSameMonth, isSameWeek, isSameDay, isSameYear,
} from 'date-fns';
import { es } from 'date-fns/locale';

// Inicio's period selector (redesign.md, Fase 4): ‹ Octubre 2026 › for monthly
// budgets, ‹ 27 oct – 2 nov › for weekly, ‹ Hoy › for daily. It drives the
// same global date range every screen uses. The current period runs to the end
// of today (matching budgetPeriodRange); past periods run to their last day.
const STEP = {
  monthly: { start: startOfMonth, end: endOfMonth, add: addMonths, same: isSameMonth },
  weekly: {
    start: (d) => startOfWeek(d, { weekStartsOn: 1 }),
    end: (d) => endOfWeek(d, { weekStartsOn: 1 }),
    add: addWeeks,
    same: (a, b) => isSameWeek(a, b, { weekStartsOn: 1 }),
  },
  daily: { start: startOfDay, end: endOfDay, add: addDays, same: isSameDay },
};

export function periodRange(budgetPeriod, anchor) {
  const step = STEP[budgetPeriod] || STEP.monthly;
  const now = new Date();
  const from = step.start(anchor);
  return { from, to: step.same(anchor, now) ? endOfDay(now) : step.end(anchor) };
}

function label(budgetPeriod, anchor) {
  const now = new Date();
  if (budgetPeriod === 'daily') {
    if (isSameDay(anchor, now)) return 'Hoy';
    if (isSameDay(anchor, addDays(now, -1))) return 'Ayer';
    return format(anchor, isSameYear(anchor, now) ? "d 'de' MMMM" : "d MMM yyyy", { locale: es });
  }
  if (budgetPeriod === 'weekly') {
    const { from } = periodRange('weekly', anchor);
    const to = endOfWeek(anchor, { weekStartsOn: 1 });
    return `${format(from, 'd MMM', { locale: es })} – ${format(to, 'd MMM', { locale: es })}`;
  }
  const month = format(anchor, 'MMMM', { locale: es });
  const text = month.charAt(0).toUpperCase() + month.slice(1);
  return isSameYear(anchor, now) ? text : `${text} ${format(anchor, 'yyyy')}`;
}

export default function PeriodStepper({ budgetPeriod = 'monthly', dateRange, setDateRange }) {
  const step = STEP[budgetPeriod] || STEP.monthly;
  const anchor = dateRange?.from || new Date();
  const isCurrent = step.same(anchor, new Date());
  const go = (n) => setDateRange(periodRange(budgetPeriod, step.add(anchor, n)));
  const btn = 'w-11 h-11 flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-30 disabled:pointer-events-none';
  return (
    <div className="flex items-center -ml-3" data-testid="period-stepper">
      <button type="button" onClick={() => go(-1)} aria-label="Periodo anterior" className={btn} data-testid="period-prev">
        <ChevronLeft className="w-5 h-5" />
      </button>
      <span className="min-w-[6.5rem] text-center text-sm font-semibold text-slate-200" data-testid="period-label">
        {label(budgetPeriod, anchor)}
      </span>
      <button type="button" onClick={() => go(1)} disabled={isCurrent} aria-label="Periodo siguiente" className={btn} data-testid="period-next">
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}
